import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { normalizarRefeicoes } from '../lib/dietaUtils';

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const [treinos, setTreinos] = useState([]);
  const [dietas, setDietas] = useState([]);
  const [exames, setExames] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [feedbacksTreino, setFeedbacksTreino] = useState([]);
  const [perfis, setPerfis] = useState({});
  const [exerciciosBiblioteca, setExerciciosBiblioteca] = useState([]);
  const [treinosBiblioteca, setTreinosBiblioteca] = useState([]);
  const [disponibilidade, setDisponibilidade] = useState({});

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setTreinos([]); setDietas([]); setExames([]); setAgendamentos([]); setCheckins([]); setFeedbacksTreino([]);
        setPerfis({}); setExerciciosBiblioteca([]); setTreinosBiblioteca([]); setDisponibilidade({});
      } else {
        carregarTodos();
      }
    });
    carregarTodos();
    return () => listener.subscription.unsubscribe();
  }, []);

  async function carregarTodos() {
    await Promise.all([
      carregarTreinos(), carregarDietas(), carregarExames(), carregarAgendamentos(),
      carregarCheckins(), carregarFeedbacksTreino(), carregarPerfis(), carregarDisponibilidade(), carregarBiblioteca(), carregarBibliotecaTreinos()
    ]);
  }

  async function carregarTreinos() {
    const { data, error } = await supabase.from('treinos').select('*').order('ordem');
    if (error) throw error;
    if (data) setTreinos(data.map(t => ({ ...t, alunaId: t.aluna_id, criadoEm: t.criado_em })));
  }

  async function carregarDietas() {
    const { data } = await supabase.from('dietas').select('*').order('criado_em');
    if (data) setDietas(data.map(d => ({ ...d, alunaId: d.aluna_id, criadoEm: d.criado_em, refeicoes: normalizarRefeicoes(d.refeicoes), listaCompras: d.lista_compras || [] })));
  }

  async function carregarExames() {
    const { data } = await supabase.from('exames').select('*').order('criado_em');
    if (data) setExames(data.map(e => ({ ...e, alunaId: e.aluna_id, criadoEm: e.criado_em, orientacoesColeta: e.orientacoes_coleta })));
  }

  async function salvarExame(exame) {
    const row = {
      id: exame.id || `ex${Date.now()}`,
      aluna_id: exame.alunaId,
      titulo: exame.titulo,
      categorias: exame.categorias || [],
      orientacoes_coleta: exame.orientacoesColeta || null,
      obs: exame.obs || null,
    };
    await supabase.from('exames').upsert(row);
    await carregarExames();
  }

  async function deletarExame(id) {
    await supabase.from('exames').delete().eq('id', id);
    await carregarExames();
  }

  async function enviarResultadoExame(exameId, resultado) {
    const exame = exames.find(e => e.id === exameId);
    const resultados = [...(exame?.resultados || []), { id: `r${Date.now()}`, ...resultado }];
    await supabase.from('exames').update({ resultados }).eq('id', exameId);
    await carregarExames();
  }

  async function removerResultadoExame(exameId, resultadoId) {
    const exame = exames.find(e => e.id === exameId);
    const resultados = (exame?.resultados || []).filter(r => r.id !== resultadoId);
    await supabase.from('exames').update({ resultados }).eq('id', exameId);
    await carregarExames();
  }

  async function carregarPerfis() {
    const { data } = await supabase.from('perfis').select('*');
    if (data) {
      const mapa = {};
      data.forEach(p => {
        mapa[p.aluna_id] = {
          telefone: p.telefone || '', cidade: p.cidade || '', pais: p.pais || 'EUA',
          dataNascimento: p.data_nascimento || '', altura: p.altura || '',
          modalidade: p.modalidade || 'nutricao-treino',
          anamnese: p.anamnese || {},
        };
      });
      setPerfis(mapa);
    }
  }

  async function carregarAgendamentos() {
    const { data } = await supabase.from('agendamentos').select('*').order('criado_em', { ascending: false });
    if (data) setAgendamentos(data.map(a => ({ ...a, alunaId: a.aluna_id, criadoEm: a.criado_em })));
  }

  async function carregarCheckins() {
    const { data } = await supabase.from('checkins').select('*').order('criado_em', { ascending: false });
    if (data) setCheckins(data.map(c => ({ ...c, alunaId: c.aluna_id, treinoId: c.treino_id, criadoEm: c.criado_em, notaGeral: c.nota_geral })));
  }

  async function carregarFeedbacksTreino() {
    const { data, error } = await supabase.from('feedbacks_treino_ciclo').select('*').order('criado_em', { ascending: false });
    if (error) {
      if (error.code !== '42P01') console.error('Erro ao carregar revisões de treino:', error);
      return;
    }
    setFeedbacksTreino((data || []).map(f => ({ ...f, alunaId: f.aluna_id, criadoEm: f.criado_em })));
  }

  async function carregarDisponibilidade() {
    const { data } = await supabase.from('disponibilidade').select('*');
    if (data) {
      const obj = {};
      data.forEach(d => { obj[d.data] = d.horarios; });
      setDisponibilidade(obj);
    }
  }

  async function salvarTreino(treino) {
    const exs = (treino.exercicios || []).map((ex, i) => ({ ...ex, id: ex.id || `e${Date.now()}${i}` }));
    const row = {
      id: treino.id || `t${Date.now()}`,
      aluna_id: treino.alunaId,
      titulo: treino.titulo,
      descricao: treino.descricao || null,
      semana: treino.semana || 1,
      ordem: treino.ordem || 1,
      obs: treino.obs || null,
      exercicios: exs,
    };
    const { data, error } = await supabase
      .from('treinos')
      .upsert(row)
      .select()
      .single();
    if (error) throw error;

    const treinoSalvo = { ...data, alunaId: data.aluna_id, criadoEm: data.criado_em };
    setTreinos(atuais => {
      const semVersaoAntiga = atuais.filter(item => item.id !== treinoSalvo.id);
      return [...semVersaoAntiga, treinoSalvo].sort((a, b) => (a.ordem || 1) - (b.ordem || 1));
    });
    return treinoSalvo;
  }

  async function deletarTreino(id) {
    await supabase.from('treinos').delete().eq('id', id);
    await carregarTreinos();
  }

  async function salvarDieta(dieta) {
    const row = {
      id: dieta.id || `d${Date.now()}`,
      aluna_id: dieta.alunaId,
      titulo: dieta.titulo,
      descricao: dieta.descricao || null,
      calorias: dieta.calorias || null,
      proteina: dieta.proteina || null,
      obs: dieta.obs || null,
      orientacoes: dieta.orientacoes || [],
      refeicoes: normalizarRefeicoes(dieta.refeicoes),
      agua: dieta.agua || null,
      suplementacao: dieta.suplementacao || [],
      lista_compras: dieta.listaCompras || [],
    };
    await supabase.from('dietas').upsert(row);
    await carregarDietas();
  }

  async function deletarDieta(id) {
    await supabase.from('dietas').delete().eq('id', id);
    await carregarDietas();
  }

  async function salvarAgendamento(ag) {
    const row = {
      id: `ag${Date.now()}`,
      aluna_id: ag.alunaId || null,
      nome: ag.nome, email: ag.email,
      telefone: ag.telefone || null,
      modalidade: ag.modalidade,
      tipo: ag.tipo || 'avaliacao',
      data: ag.data, horario: ag.horario,
      obs: ag.obs || null,
      status: 'pendente',
    };
    const { error } = await supabase.from('agendamentos').insert(row);
    if (error) {
      // 23505 = violação do índice único (data, horario) — alguém reservou esse horário primeiro.
      if (error.code === '23505') return { success: false, conflito: true };
      return { success: false, error: error.message };
    }
    await carregarAgendamentos();
    const restantes = (disponibilidade[ag.data] || []).filter(h => h !== ag.horario);
    await salvarDisponibilidade(ag.data, restantes);
    return { success: true };
  }

  async function atualizarStatusAgendamento(id, status) {
    const ag = agendamentos.find(a => a.id === id);
    await supabase.from('agendamentos').update({ status }).eq('id', id);
    await carregarAgendamentos();
    // Cancelar libera o horário de volta para outras alunas agendarem.
    if (status === 'cancelado' && ag) {
      const atuais = disponibilidade[ag.data] || [];
      if (!atuais.includes(ag.horario)) {
        await salvarDisponibilidade(ag.data, [...atuais, ag.horario].sort());
      }
    }
  }

  async function salvarCheckin(ck) {
    const row = {
      id: `ck${Date.now()}`,
      aluna_id: ck.alunaId,
      treino_id: ck.treinoId,
      exercicios: ck.exercicios || [],
      nota_geral: ck.notaGeral || null,
    };
    const { error } = await supabase.from('checkins').insert(row);
    if (error) throw error;
    await carregarCheckins();
  }

  async function salvarFeedbackTreino(feedback) {
    const row = {
      aluna_id: feedback.alunaId,
      ciclo_chave: feedback.cicloChave,
      aparelhos_disponiveis: feedback.aparelhosDisponiveis,
      realizou_todos: feedback.realizouTodos,
      dificuldade: feedback.dificuldade,
      sentiu_dor: feedback.sentiuDor,
      substituir_exercicio: feedback.substituirExercicio,
      comentario: feedback.comentario || null,
    };
    const { error } = await supabase.from('feedbacks_treino_ciclo').upsert(row, { onConflict: 'aluna_id,ciclo_chave' });
    if (error) throw error;
    await carregarFeedbacksTreino();
  }

  async function salvarPerfil(alunaId, dados) {
    const row = {
      aluna_id: alunaId,
      telefone: dados.telefone || null,
      cidade: dados.cidade || null,
      pais: dados.pais || null,
      data_nascimento: dados.dataNascimento || null,
      altura: dados.altura || null,
      modalidade: dados.modalidade || null,
      anamnese: dados.anamnese || {},
    };
    await supabase.from('perfis').upsert(row);
    setPerfis(p => ({ ...p, [alunaId]: dados }));
  }

  async function salvarDisponibilidade(data, horarios) {
    if (!horarios || horarios.length === 0) {
      await supabase.from('disponibilidade').delete().eq('data', data);
    } else {
      await supabase.from('disponibilidade').upsert({ data, horarios });
    }
    await carregarDisponibilidade();
  }

  async function carregarBiblioteca() {
    const { data } = await supabase.from('exercicios_biblioteca').select('*').order('nome');
    if (data) setExerciciosBiblioteca(data.map(e => ({
      id: e.id, nome: e.nome, grupoMuscular: e.grupo_muscular,
      videoUrl: e.video_url, instrucoes: e.instrucoes, criadoEm: e.criado_em
    })));
  }

  async function salvarExercicioBiblioteca(ex) {
    const row = {
      id: ex.id || `ex${Date.now()}`,
      nome: ex.nome,
      grupo_muscular: ex.grupoMuscular || null,
      video_url: ex.videoUrl || null,
      instrucoes: ex.instrucoes || null,
    };
    await supabase.from('exercicios_biblioteca').upsert(row);
    await carregarBiblioteca();
    return row.id;
  }

  async function deletarExercicioBiblioteca(id) {
    await supabase.from('exercicios_biblioteca').delete().eq('id', id);
    await carregarBiblioteca();
  }

  async function carregarBibliotecaTreinos() {
    const { data } = await supabase.from('treinos_biblioteca').select('*').order('protocolo').order('ordem');
    if (data) setTreinosBiblioteca(data);
  }

  async function salvarTreinoBiblioteca(t) {
    const row = {
      id: t.id || `libcustom_${Date.now()}`,
      protocolo: t.protocolo,
      categoria: t.categoria || 'principal',
      letra: t.letra || null,
      titulo: t.titulo,
      dia_semana: t.diaSemana || null,
      foco: t.foco || null,
      ordem: t.ordem || 1,
      instrucoes: t.instrucoes || null,
      exercicios: t.exercicios || [],
    };
    await supabase.from('treinos_biblioteca').upsert(row);
    await carregarBibliotecaTreinos();
    return row.id;
  }

  async function deletarTreinoBiblioteca(id) {
    await supabase.from('treinos_biblioteca').delete().eq('id', id);
    await carregarBibliotecaTreinos();
  }

  return (
    <DataContext.Provider value={{
      treinos, dietas, exames, agendamentos, checkins, feedbacksTreino, perfis, disponibilidade,
      exerciciosBiblioteca, salvarExercicioBiblioteca, deletarExercicioBiblioteca,
      treinosBiblioteca, salvarTreinoBiblioteca, deletarTreinoBiblioteca,
      salvarTreino, deletarTreino, salvarDieta, deletarDieta, salvarExame, deletarExame,
      enviarResultadoExame, removerResultadoExame,
      salvarAgendamento, atualizarStatusAgendamento,
      salvarCheckin, salvarFeedbackTreino, salvarPerfil, salvarDisponibilidade,
      recarregar: carregarTodos,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() { return useContext(DataContext); }
